import { Badge, Box, Grid, Group, List, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { ReactNode } from "react";
import { ClinicalRiskLevel } from "../utils/clinicalSummaryTypes";

export interface ClinicalSummaryDisplayMetric {
  label: string;
  value: string;
}

interface ClinicalSummaryCardProps {
  title: string;
  explainer: string;
  riskLevel: ClinicalRiskLevel;
  riskLabel: string;
  reason: string;
  keyFindingsLabel: string;
  keyFindings: string[];
  interpretationLabel: string;
  interpretationNotes: string[];
  metrics: ClinicalSummaryDisplayMetric[];
  children?: ReactNode;
}

const RISK_COLOR_MAP: Record<ClinicalRiskLevel, string> = {
  low: "green",
  monitor: "yellow",
  high: "red",
};

export default function ClinicalSummaryCard({
  title,
  explainer,
  riskLevel,
  riskLabel,
  reason,
  keyFindingsLabel,
  keyFindings,
  interpretationLabel,
  interpretationNotes,
  metrics,
  children,
}: ClinicalSummaryCardProps) {
  const riskColor = RISK_COLOR_MAP[riskLevel];

  return (
    <Paper withBorder radius="md" p="md" style={{ breakInside: "avoid", pageBreakInside: "avoid" }}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ flex: 1 }}>
            <Text fw={700} size="md">
              {title}
            </Text>
            <Text size="sm" c="dimmed">
              {explainer}
            </Text>
          </Stack>
          <Badge variant="filled" color={riskColor} size="lg" radius="sm">
            {riskLabel}
          </Badge>
        </Group>

        <Text size="sm">{reason}</Text>

        {metrics.length > 0 && (
          <Box bg="var(--oculera-surface-alt)" p="sm" style={{ borderRadius: "10px" }}>
            <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="sm" verticalSpacing="sm">
              {metrics.map((metric) => (
                <Stack key={metric.label} gap={1}>
                  <Text size="10px" c="dimmed" tt="uppercase" fw={700} lh={1.2}>
                    {metric.label}
                  </Text>
                  <Text fw={700} size="md" lh={1.1}>
                    {metric.value}
                  </Text>
                </Stack>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {(keyFindings.length > 0 || interpretationNotes.length > 0) && (
          <Grid
            gutter="sm"
            className={
              keyFindings.length > 0 && interpretationNotes.length > 0 ? "print-row-2" : undefined
            }
          >
            {keyFindings.length > 0 && (
              <Grid.Col span={{ base: 12, md: interpretationNotes.length > 0 ? 6 : 12 }}>
                <Box
                  bg="var(--oculera-surface-alt)"
                  p="sm"
                  style={{ borderRadius: "10px", height: "100%" }}
                >
                  <Stack gap={6}>
                    <Text size="10px" c="dimmed" tt="uppercase" fw={700} lh={1.2}>
                      {keyFindingsLabel}
                    </Text>
                    <List size="sm" spacing={4}>
                      {keyFindings.map((item) => (
                        <List.Item key={item}>{item}</List.Item>
                      ))}
                    </List>
                  </Stack>
                </Box>
              </Grid.Col>
            )}

            {interpretationNotes.length > 0 && (
              <Grid.Col span={{ base: 12, md: keyFindings.length > 0 ? 6 : 12 }}>
                <Box
                  bg="var(--oculera-surface-alt)"
                  p="sm"
                  style={{ borderRadius: "10px", height: "100%" }}
                >
                  <Stack gap={6}>
                    <Text size="10px" c="dimmed" tt="uppercase" fw={700} lh={1.2}>
                      {interpretationLabel}
                    </Text>
                    <List size="sm" spacing={4}>
                      {interpretationNotes.map((item) => (
                        <List.Item key={item}>{item}</List.Item>
                      ))}
                    </List>
                  </Stack>
                </Box>
              </Grid.Col>
            )}
          </Grid>
        )}

        {children}
      </Stack>
    </Paper>
  );
}
